import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotification } from '../contexts/NotificationContext';
import { apiService } from '../services/ApiService';
import Header from './Header';
import '../styles/ConfiguracaoConta.css';

const ConfiguracaoConta = () => {
  const { currentUser, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // Estado para controlar a aba ativa
  const [activeTab, setActiveTab] = useState('diarioObra');
  
  // Estados para configuração de diário de obra
  const [corPrimaria, setCorPrimaria] = useState('#0077B6');
  const [dadosConstrutora, setDadosConstrutora] = useState({
    nome: '',
    cnpj: '',
    endereco: '',
    telefone: '',
    email: '',
    responsavelTecnico: ''
  });
  
  // Estados para o logo da empresa
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // Estados para gerenciar pré-configurações de orçamento
  const [configs, setConfigs] = useState([]);
  const [configData, setConfigData] = useState({
    descricao: '',
    maoDeObraCusto: 0,
    fatorMultiplicacao: 1.7
  });
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(null);
  
  // Estados para feedback ao usuário
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  // Carregar dados iniciais
  useEffect(() => {
    carregarConfiguracaoDiarioObra();
    carregarPreConfiguracoesOrcamento();
  }, []);

  // Função para carregar configuração de diário de obra
  const carregarConfiguracaoDiarioObra = async () => {
    try {
      const resultado = await apiService.getDiarioObraConfiguracao();
      
      if (resultado.success && resultado.data) {
        // Atualizar cor primária
        if (resultado.data.corPrimaria) {
          setCorPrimaria(resultado.data.corPrimaria);
        }
        
        // Atualizar logo se existir
        if (resultado.data.logoEmpresas) {
          setLogoPreview(resultado.data.logoEmpresas);
        }
        
        // Atualizar dados da construtora
        if (resultado.data.dadosConstrutora) {
          setDadosConstrutora(resultado.data.dadosConstrutora);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações do diário de obra:', error);
    }
  };

  // Função para carregar pré-configurações de orçamento
  const carregarPreConfiguracoesOrcamento = async () => {
    try {
      // Função para normalizar string de empresa (para comparações mais robustas)
      const normalizeEmpresa = (empresa) => {
        if (!empresa) return '';
        return empresa.toString().trim().toLowerCase();
      };

      // Função para verificar se a configuração pertence à empresa do usuário
      const isConfigFromUserCompany = (config) => {
        if (!config) return false;
        
        // Admin sempre vê todas as configurações
        if (currentUser?.role === 'admin') return true;
        
        const empresaUsuario = normalizeEmpresa(currentUser?.empresa);
        const empresaConfig = normalizeEmpresa(config.empresa);
        
        // Se a config não tem empresa definida, consideramos que é visível para todos
        if (!empresaConfig) return true;
        
        // Comparação exata entre as empresas normalizadas
        return empresaConfig === empresaUsuario;
      };

      // Buscar configurações da API
      const result = await apiService.getConfiguracao();
      
      if (result.success && result.data && result.data.orcamentoConfigs && Array.isArray(result.data.orcamentoConfigs)) {
        // Garantir que todos os campos obrigatórios existam em cada configuração
        const configsValidadas = result.data.orcamentoConfigs.map(config => ({
          id: config.id || Date.now(),
          descricao: config.descricao || '',
          maoDeObraCusto: typeof config.maoDeObraCusto === 'number' ? config.maoDeObraCusto : 0,
          fatorMultiplicacao: typeof config.fatorMultiplicacao === 'number' ? config.fatorMultiplicacao : 1.7,
          empresa: config.empresa || currentUser?.empresa || 'kmf'
        }));
        
        // Filtrar para mostrar apenas as configurações da empresa do usuário atual
        const configsFiltradas = configsValidadas.filter(isConfigFromUserCompany);
        setConfigs(configsFiltradas);
      } else {
        // Tentar carregar do localStorage como fallback
        const savedConfigs = localStorage.getItem('orcamentoConfigs');
        
        if (savedConfigs) {
          try {
            const parsedConfigs = JSON.parse(savedConfigs);
            const configsFiltradas = Array.isArray(parsedConfigs) 
              ? parsedConfigs.filter(isConfigFromUserCompany)
              : [];
            setConfigs(configsFiltradas);
          } catch (e) {
            console.error('Erro ao parsear configurações do localStorage:', e);
            setConfigs([]);
          }
        } else {
          setConfigs([]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar pré-configurações de orçamento:', error);
      setConfigs([]);
    }
  };

  // Função para mudar cor primária do diário de obra
  const handleCorPrimariaChange = (e) => {
    setCorPrimaria(e.target.value);
  };

  // Função para atualizar os dados da construtora
  const handleDadosChange = (e) => {
    const { name, value } = e.target;
    setDadosConstrutora({
      ...dadosConstrutora,
      [name]: value
    });
  };

  // Função para lidar com o upload do logo
  const handleUploadLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Verificar o tipo de arquivo
    if (!file.type.match('image/*')) {
      setErro('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    // Verificar o tamanho do arquivo (limite de 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setErro('A imagem selecionada é muito grande. O limite é 2MB.');
      return;
    }

    // Criar URL para preview
    const previewURL = URL.createObjectURL(file);
    setLogoPreview(previewURL);
    setLogoFile(file);
  };

  // Função para salvar configurações do diário de obra
  const salvarConfiguracaoDiarioObra = async (e) => {
    e.preventDefault();
    
    // Resetar mensagens
    setErro(null);
    setSalvando(true);
    
    try {
      // Preparar dados de configuração
      const configData = {
        corPrimaria,
        dadosConstrutora,
        atualizadoEm: new Date().toISOString(),
        logoFile: logoFile // Passar o arquivo diretamente para o ApiService converter
      };
      
      // Salvar configurações
      const resultado = await apiService.saveDiarioObraConfiguracao(configData);
      
      if (resultado.success) {
        
        // Se a API retornou uma URL para a logo, atualizar a preview
        if (resultado.data && resultado.data.logoEmpresas) {
          setLogoPreview(resultado.data.logoEmpresas);
        }
        
        // Limpar o arquivo de logo após o upload
        setLogoFile(null);
        
        showNotification('success', 'Configurações do diário de obra salvas com sucesso!');
      } else {
        throw new Error(resultado.error || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      setErro(error.message || 'Não foi possível salvar as configurações. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  // Função para editar uma configuração de orçamento existente
  const editarConfiguracao = (config) => {
    setIsEditingConfig(true);
    setEditingConfigId(config.id);
    setConfigData({
      descricao: config.descricao || '',
      maoDeObraCusto: config.maoDeObraCusto || 0,
      fatorMultiplicacao: config.fatorMultiplicacao || 1.7
    });
    
    // Scroll suave até o formulário de edição
    const formElement = document.querySelector('.config-section form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Função para excluir uma configuração de orçamento
  const excluirConfiguracao = async (configId) => {
    if (window.confirm('Tem certeza que deseja excluir esta configuração?')) {
      try {
        // Filtrar a configuração a ser excluída
        const novasConfigs = configs.filter(c => c.id !== configId);
        
        // Atualizar estado local
        setConfigs(novasConfigs);
        
        // Salvar no localStorage e na API
        await salvarPreConfiguracoes(novasConfigs);
        
        showNotification('success', 'Configuração excluída com sucesso');
      } catch (error) {
        console.error('Erro ao excluir configuração:', error);
        showNotification('error', 'Erro ao excluir configuração');
      }
    }
  };

  // Função para adicionar ou atualizar configuração de orçamento
  const salvarConfiguracao = async (e) => {
    e.preventDefault();
    
    // Validar dados
    if (!configData.descricao.trim()) {
      setErro('A descrição é obrigatória');
      return;
    }

    try {
      let novasConfigs;
      
      if (isEditingConfig) {
        // Atualizar configuração existente
        novasConfigs = configs.map(config => {
          if (config.id === editingConfigId) {
            return {
              ...config,
              ...configData,
              empresa: currentUser?.empresa || 'kmf'
            };
          }
          return config;
        });
      } else {
        // Adicionar nova configuração
        const novaConfig = {
          id: Date.now(),
          ...configData,
          empresa: currentUser?.empresa || 'kmf'
        };
        novasConfigs = [...configs, novaConfig];
      }
      
      // Salvar configurações
      await salvarPreConfiguracoes(novasConfigs);
      
      // Resetar formulário
      setConfigData({
        descricao: '',
        maoDeObraCusto: 0,
        fatorMultiplicacao: 1.7
      });
      setIsEditingConfig(false);
      setEditingConfigId(null);
      
      showNotification('success', isEditingConfig ? 'Configuração atualizada com sucesso' : 'Nova configuração adicionada');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      setErro('Erro ao salvar configuração. Tente novamente.');
    }
  };

  // Função para salvar configurações no localStorage e na API
  const salvarPreConfiguracoes = async (novasConfigs) => {
    // Salvar localmente
    localStorage.setItem('orcamentoConfigs', JSON.stringify(novasConfigs));
    setConfigs(novasConfigs);
    
    try {
      // Garantir que cada configuração tenha a empresa do usuário e campos obrigatórios
      const configsComEmpresa = novasConfigs.map(config => ({
        ...config,
        empresa: currentUser?.empresa || 'kmf',
        descricao: config.descricao || '',
        fatorMultiplicacao: config.fatorMultiplicacao || 1.7,
        maoDeObraCusto: config.maoDeObraCusto || 0,
        id: config.id
      }));
      
      // Salvar na API
      const result = await apiService.getConfiguracao();
      
      if (result.success && result.data) {
        // Manter todos os outros dados de configuração e adicionar/atualizar orcamentoConfigs
        const updatedConfig = {
          ...result.data,
          orcamentoConfigs: configsComEmpresa
        };
        
        const updateResult = await apiService.updateConfiguracao(updatedConfig);
        
        if (updateResult.success) {
        } else {
          console.error('Erro ao salvar configurações na API:', updateResult.error);
        }
      }
    } catch (error) {
      console.error('Erro ao sincronizar configurações com a API:', error);
      throw error;
    }
  };

  // Função para cancelar edição
  const cancelarEdicao = () => {
    setConfigData({
      descricao: '',
      maoDeObraCusto: 0,
      fatorMultiplicacao: 1.7
    });
    setIsEditingConfig(false);
    setEditingConfigId(null);
  };

  // Função para navegar para home
  const navegarParaHome = () => {
    navigate('/home');
  };
  
  // Função para fazer logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`configuracao-conta-container ${darkMode ? 'dark-mode' : ''}`}>
      <Header onLogout={handleLogout} />
      
      <div className="configuracao-conta-content">
        <div className="configuracao-conta-header">
          <h1>Configurações da Conta</h1>
          <p>Gerencie suas configurações de diário de obra e orçamentos</p>
        </div>
        
        <div className="configuracao-tabs">
          <button 
            className={`tab-button ${activeTab === 'diarioObra' ? 'active' : ''}`}
            onClick={() => setActiveTab('diarioObra')}
          >
            <i className="fas fa-hard-hat"></i> Configurações da empresa
          </button>
          <button 
            className={`tab-button ${activeTab === 'orcamento' ? 'active' : ''}`}
            onClick={() => setActiveTab('orcamento')}
          >
            <i className="fas fa-file-invoice-dollar"></i> Pré-Configurações de Orçamento
          </button>
        </div>
        
        <div className="configuracao-content">
          {/* Conteúdo da aba de Diário de Obra */}
          {activeTab === 'diarioObra' && (
            <div className="diario-obra-config">
              <h2>Configurações do Diário de Obra</h2>
              
              {erro && (
                <div className="erro-mensagem">
                  <i className="fas fa-exclamation-triangle"></i> {erro}
                </div>
              )}
              
              <form onSubmit={salvarConfiguracaoDiarioObra}>
                <div className="config-section">
                  <h3>Aparência</h3>
                  
                  <div className="form-group">
                    <label htmlFor="corPrimaria">Cor Primária</label>
                    <div className="cor-selector">
                      <input
                        type="color"
                        id="corPrimaria"
                        value={corPrimaria}
                        onChange={handleCorPrimariaChange}
                      />
                      <input
                        type="text"
                        className="cor-valor"
                        value={corPrimaria}
                        onChange={(e) => {
                          const valor = e.target.value;
                          if (valor.startsWith('#') && valor.length <= 7 && /^#[0-9A-Fa-f]*$/.test(valor)) {
                            setCorPrimaria(valor);
                          }
                        }}
                        placeholder="#000000"
                      />
                    </div>
                    
                    <div className="cor-preview">
                      <span>Visualização:</span>
                      <div 
                        className="cor-amostra" 
                        style={{ backgroundColor: corPrimaria }}
                      ></div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="logo">Logo da Empresa</label>
                    <div className="logo-upload-container">
                      {logoPreview && (
                        <div className="logo-preview">
                          <img 
                            src={logoPreview} 
                            alt="Logo da empresa" 
                            className="logo-image"
                          />
                          <button 
                            type="button" 
                            className="remove-logo-btn"
                            onClick={() => {
                              setLogoPreview(null);
                              setLogoFile(null);
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        id="logo"
                        name="logo"
                        accept="image/*"
                        onChange={handleUploadLogo}
                        className={logoPreview ? "has-preview" : ""}
                      />
                      <small className="form-text">
                        Imagem de no máximo 2MB. Formatos: JPG, PNG ou GIF.
                      </small>
                    </div>
                  </div>
                </div>
                
                <div className="config-section">
                  <h3>Dados da Construtora</h3>
                  
                  <div className="form-group">
                    <label htmlFor="nome">Nome da Construtora</label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={dadosConstrutora.nome}
                      onChange={handleDadosChange}
                      placeholder="Nome da sua empresa"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="cnpj">CNPJ</label>
                    <input
                      type="text"
                      id="cnpj"
                      name="cnpj"
                      value={dadosConstrutora.cnpj}
                      onChange={handleDadosChange}
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="endereco">Endereço</label>
                    <input
                      type="text"
                      id="endereco"
                      name="endereco"
                      value={dadosConstrutora.endereco}
                      onChange={handleDadosChange}
                      placeholder="Endereço completo"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="telefone">Telefone</label>
                      <input
                        type="text"
                        id="telefone"
                        name="telefone"
                        value={dadosConstrutora.telefone}
                        onChange={handleDadosChange}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">E-mail</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={dadosConstrutora.email}
                        onChange={handleDadosChange}
                        placeholder="email@exemplo.com"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="responsavelTecnico">Responsável Técnico</label>
                    <input
                      type="text"
                      id="responsavelTecnico"
                      name="responsavelTecnico"
                      value={dadosConstrutora.responsavelTecnico}
                      onChange={handleDadosChange}
                      placeholder="Nome do responsável técnico"
                    />
                  </div>
                </div>
                
                <div className="config-actions">
                  <button 
                    type="button" 
                    className="btn-cancelar"
                    onClick={navegarParaHome}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    className="btn-salvar"
                    disabled={salvando}
                  >
                    {salvando ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Salvando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i> Salvar Configurações
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Conteúdo da aba de Pré-Configurações de Orçamento */}
          {activeTab === 'orcamento' && (
            <div className="orcamento-pre-config">
              <h2>Pré-Configurações de Orçamento</h2>
              
              {erro && (
                <div className="erro-mensagem">
                  <i className="fas fa-exclamation-triangle"></i> {erro}
                </div>
              )}
              
              <div className={`config-section ${isEditingConfig ? 'editing-mode' : ''}`}>
                <h3>
                  {isEditingConfig ? (
                    <>
                      <i className="fas fa-edit"></i> Editando Configuração
                      <span className="editing-badge">Modo de Edição</span>
                    </>
                  ) : (
                    'Nova Configuração'
                  )}
                </h3>
                
                <form onSubmit={salvarConfiguracao} className={isEditingConfig ? 'editing-form' : ''}>
                  <div className="form-group">
                    <label htmlFor="descricao">
                      Descrição*
                      {isEditingConfig && <span className="editing-indicator">Editando</span>}
                    </label>
                    <input
                      type="text"
                      id="descricao"
                      value={configData.descricao}
                      onChange={(e) => setConfigData({...configData, descricao: e.target.value})}
                      placeholder="Digite uma descrição para esta configuração"
                      className={isEditingConfig ? 'editing-input' : ''}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="maoDeObraCusto">
                      Valor de Mão de Obra Preço de Custo (R$)
                      {isEditingConfig && <span className="editing-indicator">Editando</span>}
                    </label>
                    <div className="input-with-preview">
                      <input
                        type="number"
                        id="maoDeObraCusto"
                        value={configData.maoDeObraCusto}
                        onChange={(e) => setConfigData({...configData, maoDeObraCusto: parseFloat(e.target.value) || 0})}
                        min="0"
                        step="0.01"
                        placeholder="Ex: 40.00"
                        className={isEditingConfig ? 'editing-input' : ''}
                      />
                      {isEditingConfig && (
                        <div className="value-preview">
                          <span>Valor Final: R$ {((configData.maoDeObraCusto || 0) * (configData.fatorMultiplicacao || 1.7)).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="fatorMultiplicacao">
                      Fator de Multiplicação para Lucro
                      {isEditingConfig && <span className="editing-indicator">Editando</span>}
                    </label>
                    <div className="input-with-preview">
                      <input
                        type="number"
                        id="fatorMultiplicacao"
                        value={configData.fatorMultiplicacao}
                        onChange={(e) => setConfigData({...configData, fatorMultiplicacao: parseFloat(e.target.value) || 1.7})}
                        min="1"
                        step="0.1"
                        placeholder="Ex: 1.7 (70% de lucro)"
                        className={isEditingConfig ? 'editing-input' : ''}
                      />
                      {isEditingConfig && (
                        <div className="value-preview">
                          <span>Lucro: {(((configData.fatorMultiplicacao || 1.7) - 1) * 100).toFixed(0)}%</span>
                        </div>
                      )}
                    </div>
                    <small className="form-text">
                      Para 70% de lucro, use 1.7 (multiplica o valor de custo por 1.7)
                    </small>
                  </div>
                  
                  <div className="config-actions">
                    {isEditingConfig && (
                      <button 
                        type="button" 
                        className="btn-cancelar"
                        onClick={cancelarEdicao}
                      >
                        <i className="fas fa-times"></i> Cancelar Edição
                      </button>
                    )}
                    <button 
                      type="submit" 
                      className={`btn-salvar ${isEditingConfig ? 'btn-salvar-editing' : ''}`}
                    >
                      {isEditingConfig ? (
                        <>
                          <i className="fas fa-save"></i> Salvar Alterações
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus"></i> Adicionar Configuração
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
              
              <div className="config-section">
                <h3>Configurações Salvas</h3>
                
                {configs.length === 0 ? (
                  <div className="sem-configs">
                    <p>Nenhuma configuração salva. Crie uma nova configuração acima.</p>
                  </div>
                ) : (
                  <div className="configs-list">
                    {configs.map((config) => (
                      <div 
                        key={config.id} 
                        className={`config-item ${editingConfigId === config.id ? 'being-edited' : ''}`}
                      >
                        <div className="config-item-info">
                          <h4>
                            {config.descricao}
                            {editingConfigId === config.id && (
                              <>
                                <span className="editing-badge">
                                  <i className="fas fa-pencil-alt"></i> Editando
                                </span>
                                <span className="editing-scroll-hint">
                                  <i className="fas fa-arrow-up"></i> Role para cima para editar
                                </span>
                              </>
                            )}
                          </h4>
                          <div className="config-item-details">
                            <div className="detail-group">
                              <span className="detail-label">Mão de Obra:</span>
                              <span className="detail-value">R$ {config.maoDeObraCusto.toFixed(2)}</span>
                              <span className="detail-final">
                                (Final: R$ {(config.maoDeObraCusto * config.fatorMultiplicacao).toFixed(2)})
                              </span>
                            </div>
                            <div className="detail-group">
                              <span className="detail-label">Fator:</span>
                              <span className="detail-value">{config.fatorMultiplicacao.toFixed(1)}x</span>
                              <span className="detail-final">
                                (Lucro: {((config.fatorMultiplicacao - 1) * 100).toFixed(0)}%)
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="config-item-actions">
                          <button 
                            className={`btn-editar ${editingConfigId === config.id ? 'editing' : ''}`}
                            onClick={() => editarConfiguracao(config)}
                            title={editingConfigId === config.id ? "Editando..." : "Editar configuração"}
                          >
                            <i className="fas fa-edit"></i>
                            {editingConfigId === config.id ? " Editando" : ""}
                          </button>
                          <button 
                            className="btn-excluir"
                            onClick={() => excluirConfiguracao(config.id)}
                            title="Excluir configuração"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfiguracaoConta; 